import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AttendanceContextType } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { attendanceApi } from '@/services/attendance.api';
import { peopleApi } from '@/services/people.api';
import { groupsApi } from '@/services/groups.api';
import { eventsApi } from '@/services/events.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Checks someone in.
 *
 * The context picker only appears for GROUP and EVENT — GENERAL needs
 * nothing beyond who, the same distinction the API enforces in
 * `checkInRequestSchema`.
 */
export function CheckInDialog({
  onClose,
  onCheckedIn,
}: {
  onClose: () => void;
  onCheckedIn: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [personId, setPersonId] = useState<string | null>(null);
  const [personLabel, setPersonLabel] = useState('');
  const [contextType, setContextType] = useState<AttendanceContextType>('GENERAL');
  const [contextId, setContextId] = useState<string | null>(null);
  const [contextLabel, setContextLabel] = useState('');

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      attendanceApi.checkIn({
        personId: personId!,
        contextType,
        contextId: contextType === 'GENERAL' ? null : contextId,
      }),
    onSuccess: onCheckedIn,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  const canSubmit = personId !== null && (contextType === 'GENERAL' || contextId !== null);

  return (
    <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="check-in-title">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
        noValidate
        className="dialog-body"
      >
        <h2 id="check-in-title">{t('attendance.record.checkIn')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <PersonPicker
          label={t('attendance.record.person')}
          value={personLabel}
          selectedId={personId}
          onChange={setPersonLabel}
          onPick={(id, label) => {
            setPersonId(id);
            setPersonLabel(label);
          }}
        />

        <div className="field">
          <label className="field-label" htmlFor="context-type">
            {t('attendance.record.contextType')}
          </label>
          <select
            id="context-type"
            className="field-input"
            value={contextType}
            onChange={(event) => {
              setContextType(event.target.value as AttendanceContextType);
              setContextId(null);
              setContextLabel('');
            }}
          >
            <option value="GENERAL">{t('attendance.contextType.GENERAL')}</option>
            <option value="GROUP">{t('attendance.contextType.GROUP')}</option>
            <option value="EVENT">{t('attendance.contextType.EVENT')}</option>
          </select>
        </div>

        {contextType === 'GROUP' && (
          <GroupPicker
            label={t('attendance.record.group')}
            value={contextLabel}
            onChange={setContextLabel}
            onPick={(id, label) => {
              setContextId(id);
              setContextLabel(label);
            }}
          />
        )}

        {contextType === 'EVENT' && (
          <EventPicker
            label={t('attendance.record.event')}
            value={contextLabel}
            onChange={setContextLabel}
            onPick={(id, label) => {
              setContextId(id);
              setContextLabel(label);
            }}
          />
        )}

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
          >
            {t('attendance.record.checkIn')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}

// --- Pickers ------------------------------------------------------------------
//
// Three small search pickers rather than one generic component: each one
// searches a different service through a different API client, and forcing
// them behind a shared abstraction would buy nothing a prop or two doesn't
// already give for free.

function PersonPicker({
  label,
  value,
  selectedId,
  onChange,
  onPick,
}: {
  label: string;
  value: string;
  selectedId: string | null;
  onChange: (value: string) => void;
  onPick: (id: string, label: string) => void;
}) {
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ['people', 'picker', value],
    queryFn: ({ signal }) => peopleApi.list({ search: value, limit: 8 }, signal),
    enabled: value.trim().length >= 2 && selectedId === null,
  });
  const candidates = (query.data?.data ?? []).filter((person) => !person.archivedAt);

  return (
    <div className="member-picker">
      <Field
        label={label}
        name="person-search"
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('events.registration.choosePerson')}
        required
      />
      {candidates.length > 0 && (
        <ul className="picker-results">
          {candidates.map((person) => (
            <li key={person.id}>
              <Button variant="ghost" onClick={() => onPick(person.id, person.displayName)}>
                {person.displayName}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GroupPicker({
  label,
  value,
  onChange,
  onPick,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPick: (id: string, label: string) => void;
}) {
  const query = useQuery({
    queryKey: ['groups', 'picker', value],
    queryFn: ({ signal }) => groupsApi.list({ search: value, limit: 8 }, signal),
    enabled: value.trim().length >= 2,
  });

  return (
    <div className="member-picker">
      <Field
        label={label}
        name="group-search"
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required
      />
      {(query.data?.data ?? []).length > 0 && (
        <ul className="picker-results">
          {(query.data?.data ?? []).map((group) => (
            <li key={group.id}>
              <Button variant="ghost" onClick={() => onPick(group.id, group.name)}>
                {group.name}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EventPicker({
  label,
  value,
  onChange,
  onPick,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPick: (id: string, label: string) => void;
}) {
  const query = useQuery({
    queryKey: ['events', 'picker', value],
    queryFn: ({ signal }) => eventsApi.list({ search: value, limit: 8 }, signal),
    enabled: value.trim().length >= 2,
  });

  return (
    <div className="member-picker">
      <Field
        label={label}
        name="event-search"
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required
      />
      {(query.data?.data ?? []).length > 0 && (
        <ul className="picker-results">
          {(query.data?.data ?? []).map((event) => (
            <li key={event.id}>
              <Button variant="ghost" onClick={() => onPick(event.id, event.name)}>
                {event.name}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
